// Practice Details → "Outreach templates": the texts, emails and call scripts this
// practice sends or reads to patients about a balance, in three sections by kind.
//
// Each section lists the practice's own rows first ("This practice"), then the
// global defaults every practice inherits ("Default"; "Overridden" when this
// practice has a row of the same name). A biller edits, disables and deletes the
// practice's rows and can override a default (a practice row prefilled from it);
// only a super_admin edits a default itself, because that change reaches every
// practice that has not overridden it.
//
// The editor previews through POST …/templates/preview, which renders SAMPLE
// figures and touches no patient, so nothing on this page carries PHI. The server
// is the validator (name 1–120, body limits, merge fields allowed for the kind, the
// STOP line on texts); its sentence is shown word for word.
//
// Mounted by PracticeDetailsPage for staff only (never in the client view):
//
//   <TemplatesTab practiceId={selectedPractice.id} isSuperAdmin={isSuperAdmin} />

import React, { useCallback, useEffect, useRef, useState } from "react";
import useCapabilities from "../../hooks/useCapabilities";
import {
  CALL_SCRIPT_ONLY_FIELDS,
  STOP_LINE,
  TEMPLATE_KINDS,
  createTemplate,
  deleteTemplate,
  excerpt,
  fetchTemplateRows,
  kindLabelOf,
  mergeFieldsFor,
  previewTemplate,
  templatePayload,
  updateTemplate,
} from "./templatesApi";
import "./TemplatesTab.css";

// Same key index.js exports as `feature`; named here rather than imported from
// index.js, which imports this file.
const FEATURE = "collections";

function Message({ tone, children, onClose }) {
  return (
    <div className={`ot-message ${tone}`} role={tone === "error" ? "alert" : "status"}>
      <span>{children}</span>
      {onClose && (
        <button
          type="button"
          className="ot-message-close"
          aria-label="Dismiss"
          onClick={onClose}
        >
          ×
        </button>
      )}
    </div>
  );
}

function Chip({ tone = "muted", children }) {
  return <span className={`ot-chip ${tone}`}>{children}</span>;
}

const Field = ({ name }) => <code className="ot-merge-field">{`{${name}}`}</code>;

// ---- editor ----

function formFrom(kind, template) {
  const t = template || {};
  return {
    name: t.name || "",
    description: t.description || "",
    subject: kind === "email" ? t.subject || "" : "",
    body: t.body || "",
  };
}

// The draft the preview rendered. Any edit drops the preview, so the sample shown
// is always the text in the box.
const draftKey = (form) => JSON.stringify([form.body, form.subject]);

function TemplateEditor({ practiceId, kind, mode, template, onClose, onSaved }) {
  // mode: "create" (empty or prefilled from a default) | "edit" (a row by id).
  const kindDef = TEMPLATE_KINDS.find((k) => k.key === kind) || TEMPLATE_KINDS[0];
  const [form, setForm] = useState(() => formFrom(kind, template));
  const [preview, setPreview] = useState(null); // {key, body, subject, chars, segments}
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const busy = previewing || saving;
  const key = draftKey(form);
  const previewCurrent = preview && preview.key === key;
  const canPreview = form.body.trim().length > 0;
  const editingGlobal = mode === "edit" && template && template.scope === "global";

  const title =
    mode === "edit"
      ? `Edit "${template.name}"`
      : template
        ? `Override "${template.name}" for this practice`
        : `New ${kindLabelOf(kind)} template`;

  const runPreview = async () => {
    if (inFlight.current || !canPreview) return;
    inFlight.current = true;
    setPreviewing(true);
    setError(null);
    try {
      const rendered = await previewTemplate(practiceId, {
        kind,
        body: form.body,
        subject: form.subject,
      });
      setPreview({ key, ...rendered });
    } catch (err) {
      setPreview(null);
      setError(err.message);
    } finally {
      inFlight.current = false;
      setPreviewing(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (inFlight.current || busy) return;
    if (!form.name.trim()) {
      setError("Give the template a name.");
      return;
    }
    if (!form.body.trim()) {
      setError("Write the template text.");
      return;
    }
    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      // An edit sends the WHOLE form (the contract asks for no changed-fields diff
      // here); a create adds the kind.
      const saved =
        mode === "edit"
          ? await updateTemplate(practiceId, template.id, templatePayload(kind, form))
          : await createTemplate(practiceId, kind, form);
      onSaved(saved);
    } catch (err) {
      // 409 (a duplicate name) and 400 (a field the kind cannot hold, a body too
      // long) carry the server's sentence.
      setError(err.message);
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div
        className="create-modal ot-editor"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ot-editor-title"
        data-testid="template-editor"
      >
        <h2 id="ot-editor-title">{title}</h2>
        {editingGlobal && (
          <Message tone="warn">
            This is a global default. Your change reaches every practice that has not
            overridden it.
          </Message>
        )}
        {error && (
          <Message tone="error" onClose={() => setError(null)}>
            {error}
          </Message>
        )}
        <form onSubmit={save}>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="ot-name">Name</label>
              <input
                id="ot-name"
                name="name"
                value={form.name}
                onChange={set("name")}
                disabled={busy}
                maxLength={120}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="ot-description">Description</label>
              <input
                id="ot-description"
                name="description"
                value={form.description}
                onChange={set("description")}
                disabled={busy}
                maxLength={200}
                placeholder="What this template is for"
                autoComplete="off"
              />
            </div>
          </div>
          {kind === "email" && (
            <div className="form-row">
              <div className="form-field">
                <label htmlFor="ot-subject">Subject</label>
                <input
                  id="ot-subject"
                  name="subject"
                  value={form.subject}
                  onChange={set("subject")}
                  disabled={busy}
                  maxLength={200}
                  autoComplete="off"
                />
              </div>
            </div>
          )}
          <div className="form-row">
            <div className="form-field">
              <label htmlFor="ot-body">
                {kind === "sms" ? "Text" : kind === "email" ? "Message" : "Script"}
              </label>
              <textarea
                id="ot-body"
                name="body"
                className="ot-body-input"
                rows={kind === "sms" ? 4 : 10}
                value={form.body}
                onChange={set("body")}
                disabled={busy}
                maxLength={kindDef.bodyMax}
              />
              <div className="ot-count" data-testid="template-chars">
                {form.body.length.toLocaleString("en-US")} / {kindDef.bodyMax.toLocaleString("en-US")} chars
              </div>
            </div>
          </div>

          <div className="ot-legend" data-testid="merge-legend">
            <strong>Merge fields</strong>
            <div className="ot-legend-fields">
              {mergeFieldsFor(kind).map((f) => (
                <Field key={f} name={f} />
              ))}
            </div>
            <small className="ot-hint">
              {kind === "call_script" ? (
                <>
                  <Field name={CALL_SCRIPT_ONLY_FIELDS[0]} /> and{" "}
                  <Field name={CALL_SCRIPT_ONLY_FIELDS[1]} /> are allowed only in a call
                  script, which staff read and never send. A date of service never goes
                  in a text or email.
                </>
              ) : (
                <>
                  First name and balance only — never a diagnosis, provider or date of
                  service. A date of service never goes in a text or email, so{" "}
                  <Field name={CALL_SCRIPT_ONLY_FIELDS[0]} /> and{" "}
                  <Field name={CALL_SCRIPT_ONLY_FIELDS[1]} /> are refused here.
                </>
              )}
              {kind === "sms" && (
                <>
                  {" "}
                  Every text ends with "{STOP_LINE}" — it is appended on save when
                  missing.
                </>
              )}
            </small>
          </div>

          {previewCurrent && (
            <div className="ot-preview" data-testid="template-preview">
              <div className="ot-preview-head">
                <strong>Preview with sample figures</strong>
                <span className="ot-muted">
                  {preview.chars != null ? `${preview.chars} chars` : ""}
                  {kind === "sms" && preview.segments != null
                    ? ` · ${preview.segments} segment${preview.segments === 1 ? "" : "s"}`
                    : ""}
                </span>
              </div>
              {preview.subject && <p className="ot-preview-subject">{preview.subject}</p>}
              <p className="ot-preview-body">{preview.body}</p>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={runPreview}
              disabled={!canPreview || busy}
              title={
                canPreview
                  ? "Render the text with sample figures"
                  : "Write the text first"
              }
            >
              {previewing ? "Rendering…" : "Preview"}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- list ----

function TemplateRow({ row, isSuperAdmin, busy, onEdit, onOverride, onToggle, onDelete }) {
  const mine = row.scope === "practice";
  return (
    <tr
      className={row.enabled ? "" : "ot-row-disabled"}
      data-testid="template-row"
      data-id={row.id}
      data-scope={row.scope}
    >
      <td className="ot-cell-name">
        <div className="ot-name-line">
          <strong>{row.name}</strong>
          {mine ? (
            <Chip tone="info">This practice</Chip>
          ) : (
            <Chip tone="muted">Default</Chip>
          )}
          {!mine && row.overriddenBy != null && <Chip tone="warn">Overridden</Chip>}
        </div>
        {row.description && <div className="ot-muted">{row.description}</div>}
      </td>
      <td className="ot-cell-excerpt">
        {row.subject && <div className="ot-excerpt-subject">{row.subject}</div>}
        <div className="ot-excerpt">{excerpt(row.body)}</div>
      </td>
      <td className="ot-cell-status">
        <Chip tone={row.enabled ? "good" : "muted"}>{row.enabled ? "On" : "Off"}</Chip>
      </td>
      <td className="ot-cell-actions">
        {mine && (
          <>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onEdit(row)}
              disabled={busy}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => onToggle(row)}
              disabled={busy}
            >
              {row.enabled ? "Disable" : "Enable"}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm ot-danger"
              onClick={() => onDelete(row)}
              disabled={busy}
            >
              Delete
            </button>
          </>
        )}
        {!mine && row.overriddenBy == null && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onOverride(row)}
            disabled={busy}
            title="Copy this default into a template of this practice's own"
          >
            Override for this practice
          </button>
        )}
        {/* A code constant (negative id) has no row to edit. */}
        {!mine && isSuperAdmin && !row.isCodeDefault && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => onEdit(row)}
            disabled={busy}
            title="Edit the global default (every practice that has not overridden it)"
          >
            Edit
          </button>
        )}
      </td>
    </tr>
  );
}

function Section({ kindDef, rows, error, isSuperAdmin, busyId, onNew, ...actions }) {
  // Practice rows first, then the defaults; within each group the server's order.
  const ordered = rows
    ? [
        ...rows.filter((r) => r.scope === "practice"),
        ...rows.filter((r) => r.scope !== "practice"),
      ]
    : null;
  return (
    <section className="ot-section" data-testid={`templates-section-${kindDef.key}`}>
      <div className="ot-section-head">
        <h3>{kindDef.label}</h3>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onNew(kindDef.key)}
        >
          New template
        </button>
      </div>
      {error && <Message tone="error">{error}</Message>}
      {!error && ordered === null && <p className="ot-muted">Loading…</p>}
      {ordered && ordered.length === 0 && (
        <p className="ot-muted">No {kindDef.label.toLowerCase()} templates.</p>
      )}
      {ordered && ordered.length > 0 && (
        <table className="ot-table">
          <thead>
            <tr>
              <th>Template</th>
              <th>Text</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ordered.map((row) => (
              <TemplateRow
                key={row.id}
                row={row}
                isSuperAdmin={isSuperAdmin}
                busy={busyId === row.id}
                {...actions}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const emptyByKind = () =>
  Object.fromEntries(TEMPLATE_KINDS.map((k) => [k.key, null]));

export default function TemplatesTab({ practiceId, isSuperAdmin = false }) {
  const { features, loading: gateLoading, error: gateError } = useCapabilities(practiceId);
  const featureOn = !!features[FEATURE];

  const [rows, setRows] = useState(emptyByKind);
  const [loadErrors, setLoadErrors] = useState(emptyByKind);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [editor, setEditor] = useState(null); // {kind, mode, template}
  const [busyId, setBusyId] = useState(null);
  const ticket = useRef(0);

  const loadKind = useCallback(
    async (kind) => {
      const mine = ticket.current;
      try {
        const list = await fetchTemplateRows(practiceId, kind);
        if (mine !== ticket.current) return;
        setRows((r) => ({ ...r, [kind]: list }));
        setLoadErrors((e) => ({ ...e, [kind]: null }));
      } catch (err) {
        if (mine !== ticket.current) return;
        setLoadErrors((e) => ({ ...e, [kind]: err.message }));
      }
    },
    [practiceId],
  );

  useEffect(() => {
    ticket.current += 1;
    setRows(emptyByKind());
    setLoadErrors(emptyByKind());
    setNotice(null);
    setError(null);
    setEditor(null);
    if (!practiceId || !featureOn) return undefined;
    TEMPLATE_KINDS.forEach((k) => loadKind(k.key));
    return () => {
      ticket.current += 1;
    };
  }, [practiceId, featureOn, loadKind]);

  const replaceRow = (kind, saved) =>
    setRows((r) => ({
      ...r,
      [kind]: (r[kind] || []).map((row) => (row.id === saved.id ? saved : row)),
    }));

  const onToggle = async (row) => {
    if (busyId != null) return;
    setBusyId(row.id);
    setError(null);
    setNotice(null);
    try {
      const saved = await updateTemplate(practiceId, row.id, { enabled: !row.enabled });
      replaceRow(row.kind, saved);
      setNotice(`"${saved.name}" is ${saved.enabled ? "on" : "off"}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (row) => {
    if (busyId != null) return;
    const ok = window.confirm(
      `Delete "${row.name}"? Messages already sent from it keep their record.`,
    );
    if (!ok) return;
    setBusyId(row.id);
    setError(null);
    setNotice(null);
    try {
      const result = await deleteTemplate(practiceId, row.id);
      if (result.deleted) {
        setRows((r) => ({
          ...r,
          [row.kind]: (r[row.kind] || []).filter((x) => x.id !== row.id),
        }));
        setNotice(`Deleted "${row.name}".`);
        // A deleted override uncovers its default: its "Overridden" badge goes.
        loadKind(row.kind);
      } else {
        replaceRow(row.kind, result.template);
        setNotice(
          row.scope === "global"
            ? `"${row.name}" is a global default, so it was switched off instead of deleted.`
            : `"${row.name}" has been used in messages, so it was switched off instead of deleted.`,
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const onSaved = (saved) => {
    const kind = saved.kind || editor.kind;
    setEditor(null);
    setNotice(`Saved "${saved.name}".`);
    // Show the saved row at once, then reload the kind: a new override or a rename
    // changes which default reads "Overridden".
    replaceRow(kind, saved);
    loadKind(kind);
  };

  if (gateLoading) return <p className="ot-muted">Loading…</p>;
  if (gateError) {
    return (
      <div className="ot-templates" data-testid="outreach-templates-tab">
        <Message tone="error">{gateError}</Message>
      </div>
    );
  }
  if (!featureOn) {
    return (
      <div className="ot-templates" data-testid="outreach-templates-tab">
        <p className="ot-muted">
          Outreach rides the collections feature, which is off for this practice.
        </p>
      </div>
    );
  }

  return (
    <div className="ot-templates" data-testid="outreach-templates-tab">
      <p className="ot-lede">
        What this practice texts, emails or reads aloud to a patient about a balance.
        Defaults are shared by every practice; override one to change the wording here
        without touching anyone else's.
      </p>

      {error && (
        <Message tone="error" onClose={() => setError(null)}>
          {error}
        </Message>
      )}
      {notice && (
        <Message tone="success" onClose={() => setNotice(null)}>
          {notice}
        </Message>
      )}

      {TEMPLATE_KINDS.map((kindDef) => (
        <Section
          key={kindDef.key}
          kindDef={kindDef}
          rows={rows[kindDef.key]}
          error={loadErrors[kindDef.key]}
          isSuperAdmin={isSuperAdmin}
          busyId={busyId}
          onNew={(kind) => setEditor({ kind, mode: "create", template: null })}
          onEdit={(row) => setEditor({ kind: row.kind, mode: "edit", template: row })}
          onOverride={(row) => setEditor({ kind: row.kind, mode: "create", template: row })}
          onToggle={onToggle}
          onDelete={onDelete}
        />
      ))}

      {editor && (
        <TemplateEditor
          key={`${editor.mode}-${editor.kind}-${editor.template ? editor.template.id : "new"}`}
          practiceId={practiceId}
          kind={editor.kind}
          mode={editor.mode}
          template={editor.template}
          onClose={() => setEditor(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
